import EventCalendar from "components/calendar/EventCalendar";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  ModalOverlay,
} from "@chakra-ui/modal";
import { useDisclosure } from "@chakra-ui/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import ServiceForm from "./service-form";
import AssignForm from "./assign-form";
import { FiSearch } from "react-icons/fi";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import ServiceDetails from "./service-details";
import { useDarkmode } from "hooks/useTheme";
import PlusIcon from "components/icons/PlusIcon";
import ChevronLeft from "components/icons/ChevronLeft";
import ChevronRight from "components/icons/ChevronRight";
import MiniCalendar from "components/calendar/MiniCalendar";
import AtriumApi from "api";
import Loader from "components/loader/Loader";
import { useDebounce } from "components/debounce";
import toast from "react-hot-toast";

const Calendar = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!params.id,
    onClose: () => {
      navigate("/admin/service-scheduling");
    },
  });

  const ref = useRef();
  const calendarRef = useRef(null);
  const miniCalendarRef = useRef(null);

  const location = useLocation();
  const [service, setService] = useState({
    name: "",
    description: "",
    type: "",
    address: "",
    location: {
      type: "Point",
      coordinates: [0, 0],
    },
    date: "",
    startAt: "",
    endAt: "",
    schedule: "",
  });

  const [readonlyForm, setReadonlyForm] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const { darkmode } = useDarkmode();
  // const [services, setServices] = useState([]);

  const [selected, setSelected] = useState([]);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [title, setTitle] = useState("");

  const [activeDate, setActiveDate] = useState();
  const [churchServices, setChurchServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debounceSearch = useDebounce(searchTerm, 500);

  const events = useMemo(() => {
    const colors = [
      "rgba(234, 243, 255, 1)",
      "rgba(242, 248, 249, 1)",
      "rgba(252, 248, 245, 1)",
    ];
    return churchServices?.map((service, i) => {
      const date = new Date(service?.date);
      const [startHour, startMinute] = service?.startAt.split(":").map(Number);
      const [endHour, endMinute] = service?.endAt.split(":").map(Number);

      const start = new Date(date);
      start.setHours(startHour, startMinute || 0, 0, 0);

      const end = new Date(date);
      end.setHours(endHour, endMinute || 0, 0, 0);

      return {
        id: service?.id,
        title: service?.name,
        borderColor: "transparent",
        start,
        end,
        backgroundColor: colors[i % colors.length], //colors[Math.floor(Math.random() * 10000) % colors.length],
        className: "info",
        origin: service,
      };
    });
  }, [churchServices]);

  const onSave = useMemo(() => {
    return (model) => {
      const index = churchServices?.findIndex((m) => m?.id === model?.id);

      if (index !== -1) {
        setChurchServices(
          churchServices
            .slice(0, index)
            .concat(churchServices?.slice(index + 1))
            .concat([{ ...model }])
        );
      } else {
        setChurchServices(churchServices?.concat([model]));
      }
    };
  }, [churchServices, setChurchServices]);

  const onDelete = (id) => {
    const index = churchServices?.findIndex((m) => m.id === id);

    if (index !== -1) {
      setChurchServices(
        churchServices?.slice(0, index).concat(churchServices?.slice(index + 1))
      );
    }
    onClose();
  };

  useEffect(() => {
    setReadonlyForm(
      matchPath(
        "/admin/service-scheduling/services/:id/edit",
        location.pathname
      ) === null
    );
  }, [location]);

  useEffect(() => {
    if (!service?.id && params.id && params.id !== "new") {
      AtriumApi.get(`/v1/services/${params.id}`).then((resp) => {
        setSelected(resp?.data?.service?.users);
        setService(resp?.data?.service);
      });
    }
  }, [service, params]);

  // api to get the services

  const queryBuilder = useCallback(() => {
    const query = new URLSearchParams();

    if (debounceSearch.trim()) {
      query.append("name", debounceSearch.trim());
      return `/v1/services?${query.toString()}`;
    }

    return `v1/services`;
  }, [debounceSearch]);

  const getServices = useCallback(async () => {
    try {
      setLoading(true);

      const endpoint = queryBuilder();
      const response = await AtriumApi.get(endpoint);
      if (response) {
        setLoading(false);
        setChurchServices(response?.data?.services ?? []);
        // setChurchServices(response?.data?.services);
      }
    } catch (error) {
      setLoading(false);
      console.log("error while fetching the services", error);
    }
  }, [queryBuilder]);

  useEffect(() => {
    if (activeDate) {
      getServices();
    }
  }, [activeDate, debounceSearch, getServices]);

  const newService = () => {
    setService({});
    setSelected([]);
    setReadonlyForm(false);
    onOpen();
    navigate("/admin/service-scheduling/services/new");
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const overHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.add("border-2");
        cardInner.classList.add("border-brand-500");
        cardInner.classList.remove("border-[transparent]");
        cardInner.classList.add("pointer-events-none");
        event.dataTransfer.dropEffect = "link";
        event.preventDefault();
      }
    };

    const leaveHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card === event.target) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.remove("pointer-events-none");
        cardInner.classList.remove("border-brand-500");
        cardInner.classList.add("border-[transparent]");
      }

      if (card) {
        event.preventDefault();
      }
    };

    const dropHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card === event.target) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.remove("pointer-events-none");
        cardInner.classList.remove("border-brand-500");
        cardInner.classList.add("border-[transparent]");
        const id = parseInt(cardInner.getAttribute("data-id"));
        const userId = parseInt(event.dataTransfer.getData("text"));
        if (isNaN(userId)) return;
        const index = churchServices?.findIndex((model) => model.id === id);
        if (index === -1) return;

        const service = churchServices[index];
        if (service?.users?.findIndex((user) => user?.id === userId) !== -1)
          return;

        cardInner.classList.add("animate-pulse");
        setLoading(true);
        AtriumApi.put(`/v1/services/${id}/people`, {
          users: (service?.users || [])?.map((u) => u?.id)?.concat([userId]),
        })
          .then((response) => {
            // onSave(response?.data?.service);
            cardInner.classList.remove("animate-pulse");
            toast.success("People assigned successfully");
            getServices();
          })
          .finally(() => {
            setLoading(false);
          });
      }
    };

    el.addEventListener("dragenter", overHandler);
    el.addEventListener("dragover", overHandler);
    el.addEventListener("dragleave", leaveHandler);
    el.addEventListener("drop", dropHandler);
    return () => {
      el.removeEventListener("dragenter", overHandler);
      el.removeEventListener("dragover", overHandler);
      el.removeEventListener("dragleave", leaveHandler);
      el.removeEventListener("drop", dropHandler);
    };
  }, [ref, onSave, churchServices, getServices]);

  const prevWeek = () => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.prev();

    const newDate = calendarApi.getDate();
    setActiveDate(newDate);
    setTitle(calendarApi.view.title);
  };

  const nextWeek = () => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.next();

    const newDate = calendarApi.getDate();
    setActiveDate(newDate);
    setTitle(calendarApi.view.title);
  };

  const gotoDate = (date) => {
    if (!calendarRef.current) return;
    calendarRef.current.getApi().gotoDate(date);
    setActiveDate(calendarRef.current?.getApi().getDate());
    setTitle(calendarRef.current?.getApi().view.title);
  };

  useEffect(() => {
    if (!calendarRef.current) return;
    setActiveDate(calendarRef.current?.getApi().getDate());
    setTitle(calendarRef.current?.getApi().view.title);
  }, [calendarRef]);

  // useEffect(() => {
  //   if (!activeDate) return;

  //   AtriumApi.get("/v1/services", {
  //     params: { date: activeDate.toLocaleDateString(["en-US"]) },
  //   }).then((resp) => {
  //     setServices(resp.data.services);
  //   });
  // }, [activeDate]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        miniCalendarRef.current &&
        !miniCalendarRef.current.contains(event.target)
      ) {
        setOpenCalendar(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [miniCalendarRef, setOpenCalendar]);

  return (
    <div className="mt-3 h-full w-full gap-2 rounded-[20px] md:px-3 2xl:px-4 4xl:px-6 flex flex-col -z-4">
      <div className="flex flex-row flex-wrap gap-3">
        <div
          onClick={() => setOpenCalendar(!openCalendar)}
          className="relative text-2xl font-bold content-center"
        >
          {title}
          {openCalendar && (
            <div
              className="absolute left-0 top-auto z-[1]"
              ref={miniCalendarRef}
            >
              <MiniCalendar value={activeDate} onChange={(v) => gotoDate(v)} />
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            className="bg-gray-100 rounded-2xl w-12 h-12 text-gray-700 dark:bg-navy-700 dark:text-white"
            onClick={prevWeek}
          >
            <ChevronLeft />
          </button>
          <button
            className="bg-gray-100 rounded-2xl w-12 h-12 text-gray-700 dark:bg-navy-700 dark:text-white"
            onClick={nextWeek}
          >
            <ChevronRight />
          </button>
        </div>
        <div className="select-none flex-1 flex justify-end align-end content-end">
          <div className="flex w-full h-12 max-w-80 items-center rounded-2xl bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Search for service"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark::placeholder:!text-gray-700"
            />
          </div>
        </div>
        <div className="flex gap-3 content-center">
          <GrayButton
            onClick={() => {
              setShowAssign(true);
              setSelected([]);
            }}
          >
            <svg
              className="inline-block align-middle mr-2"
              width="20"
              height="18"
              viewBox="0 0 20 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.99984 11.9167H6.24984C5.08687 11.9167 4.50538 11.9167 4.03222 12.0602C2.96688 12.3834 2.1332 13.217 1.81004 14.2824C1.6665 14.7555 1.6665 15.337 1.6665 16.5M15.8332 16.5V11.5M13.3332 14H18.3332M12.0832 5.25C12.0832 7.32107 10.4042 9 8.33317 9C6.2621 9 4.58317 7.32107 4.58317 5.25C4.58317 3.17893 6.2621 1.5 8.33317 1.5C10.4042 1.5 12.0832 3.17893 12.0832 5.25Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="inline-block align-middle">Assign people</span>
          </GrayButton>
          <DarkButton onClick={newService}>
            <span className="inline-block align-middle mr-2">
              <PlusIcon />
            </span>
            <span className="inline-block align-middle">Create service</span>
          </DarkButton>
        </div>
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : (
        <>
          <div className="z-0 h-full w-full rounded-[20px] bg-[transparent] bg-clip-border py-[20px] shadow-atrium shadow-shadow-500 dark:text-white dark:shadow-none">
            <div className="flex flex-row">
              <div
                className={`flex-1 relative w-full ${
                  showAssign ? "h-[calc(100dvh_-_270px)]" : "h-full"
                }`}
              >
                <div
                  ref={ref}
                  className={`${
                    showAssign
                      ? "absolute w-full h-full left-0 top-0 overflow-auto"
                      : ""
                  } w-full dark:text-white`}
                >
                  <EventCalendar
                    ref={calendarRef}
                    initialDate={activeDate}
                    calendarData={events}
                    select={(selectionInfo) => {
                      // setService({
                      //   date: selectionInfo.start.toLocaleDateString(["en-US"]),
                      //   startAt: selectionInfo.start.toLocaleTimeString(
                      //     ["en-US"],
                      //     {
                      //       hour: "2-digit",
                      //       minute: "2-digit",
                      //     }
                      //   ),
                      //   endAt: selectionInfo.end.toLocaleTimeString(["en-US"], {
                      //     hour: "2-digit",
                      //     minute: "2-digit",
                      //   }),
                      // });
                      // setSelected([]);
                      // setReadonlyForm(false);
                      // onOpen();
                      // navigate("/admin/service-scheduling/services/new");
                    }}
                    eventClick={({ event }) => {
                      setService(event.extendedProps.origin);
                      setSelected(event.extendedProps.origin.users);
                      setReadonlyForm(true);
                      onOpen();
                      navigate(
                        "/admin/service-scheduling/services/" + event.id
                      );
                    }}
                  />
                </div>
              </div>

              {showAssign && (
                <div className="select-none w-80 h-[calc(100dvh_-_270px)] overflow-auto flex-none px-6 relative transition-all duration-1000">
                  <button
                    className="absolute top-0 right-3 text-gray-800 text-xl p1-2 dark:text-white"
                    onClick={() => setShowAssign(false)}
                  >
                    &#x2715;
                  </button>
                  <h2 className="text-gray-800 font-bold text-lg dark:text-white">
                    Assign people
                  </h2>
                  <p className="text-gray-700 text-base">
                    Drag and drop to service card to assign
                  </p>
                  <AssignForm
                    draggable={true}
                    selected={selected}
                    setSelected={setSelected}
                    churchServices={churchServices}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* service form modal */}
      <Drawer isOpen={isOpen} onClose={onClose}>
        <ModalOverlay
          bg={darkmode ? "#A3AED080" : "#2B367480"}
          backdropFilter="blur(3px)"
          zIndex={15}
          onClick={() => onClose()}
        />
        <DrawerContent
          containerProps={{ zIndex: 20 }}
          className="my-2 w-[calc(100vw_-_32px)] max-w-[calc(100vw_-_32px)] rounded-2xl bg-white shadow-[-20px_17px_40px_4px_rgba(112,_144,_176,_0.18)] dark:bg-background-900 dark:shadow-[-22px_32px_51px_4px_#0B1437] md:w-[600px] md:max-w-[540px]"
        >
          <DrawerCloseButton className="absolute right-4 top-4 text-xs p-3 text-gray-900 dark:text-white" />

          {params.id === "new" ? (
            <ServiceForm
              onClose={onClose}
              selected={selected}
              setSelected={setSelected}
              service={service}
              setService={setService}
              getServices={getServices}
              type={params.id === "new" ? "create" : "edit"}
            />
          ) : readonlyForm ? (
            <ServiceDetails
              onClose={onClose}
              service={service}
              onDelete={onDelete}
              getServices={getServices}
              churchServices={churchServices}
              type="editTemplate"
            />
          ) : (
            <ServiceForm
              onClose={onClose}
              onSave={onSave}
              selected={selected}
              setSelected={setSelected}
              service={service}
              setService={setService}
              getServices={getServices}
            />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Calendar;
